import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Eye, EyeOff, AlertCircle, CheckCircle2 } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { resetPassword } from "@/services/auth";
import { toast } from "sonner";

interface SetNewPasswordFormProps {
  onComplete: () => void; // callback to go back to Login page
  onBack?: () => void; // callback to go back to login screen
}

const SetNewPasswordForm: React.FC<SetNewPasswordFormProps> = ({
  onComplete,
  onBack,
}) => {
  const searchParams = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);
  const [touched, setTouched] = useState({ password: false, confirmPassword: false });
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Get token from URL
  const token = searchParams.get("token") || "";

  // Validate password
  const validatePassword = (pwd: string): string | null => {
    if (!pwd) {
      return "Password is required";
    }
    if (pwd.length < 8) {
      return "Password must be at least 8 characters long";
    }
    if (!/(?=.*[a-z])/.test(pwd)) {
      return "Password must contain at least one lowercase letter";
    }
    if (!/(?=.*[A-Z])/.test(pwd)) {
      return "Password must contain at least one uppercase letter";
    }
    if (!/(?=.*\d)/.test(pwd)) {
      return "Password must contain at least one number";
    }
    return null;
  };

  // Validate confirm password
  const validateConfirmPassword = (confirm: string, pwd: string): string | null => {
    if (!confirm) {
      return "Please confirm your password";
    }
    if (confirm !== pwd) {
      return "Passwords do not match";
    }
    return null;
  };

  // Handle password change
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setError(null);
    
    if (touched.password) {
      const passwordErr = validatePassword(newPassword);
      setPasswordError(passwordErr);
    }

    // Also validate confirm password if it's been touched
    if (touched.confirmPassword && confirmPassword) {
      const confirmErr = validateConfirmPassword(confirmPassword, newPassword);
      setConfirmPasswordError(confirmErr);
    }
  };

  // Handle confirm password change
  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    setError(null);
    
    if (touched.confirmPassword) {
      const confirmErr = validateConfirmPassword(newConfirmPassword, password);
      setConfirmPasswordError(confirmErr);
    }
  };

  // Handle blur events
  const handlePasswordBlur = () => {
    setTouched(prev => ({ ...prev, password: true }));
    const passwordErr = validatePassword(password);
    setPasswordError(passwordErr);
  };

  const handleConfirmPasswordBlur = () => {
    setTouched(prev => ({ ...prev, confirmPassword: true }));
    const confirmErr = validateConfirmPassword(confirmPassword, password);
    setConfirmPasswordError(confirmErr);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mark both fields as touched
    setTouched({ password: true, confirmPassword: true });

    // Validate both fields
    const passwordErr = validatePassword(password);
    const confirmErr = validateConfirmPassword(confirmPassword, password);

    setPasswordError(passwordErr);
    setConfirmPasswordError(confirmErr);

    if (passwordErr || confirmErr) {
      return;
    }

    if (!token) {
      setError("Reset token is missing. Please use the link from your email.");
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      await resetPassword(token, password, confirmPassword);
      setSuccess(true);
      toast.success("Password reset successfully! You can now login with your new password.");
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        onComplete();
      }, 2000);
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || "Failed to reset password. Please try again.");
      toast.error(error.message || "Failed to reset password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Check if token exists on mount
  useEffect(() => {
    if (!token) {
      setError("Invalid or missing reset token. Please use the link from your email.");
    }
  }, [token]);

  if (success) {
    return (
      <div className="flex flex-col items-center gap-6 mt-8 text-center">
        <div className="p-4 rounded-full bg-green-100 dark:bg-green-900/20">
          <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold">Password Reset Successful!</h1>
          <p className="text-muted-foreground text-sm">
            Your password has been reset successfully. Redirecting to login...
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      className="flex flex-col gap-6 mt-8"
      onSubmit={handleSubmit}
    >
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold">Set New Password</h1>
        <p className="text-muted-foreground text-sm text-balance">
          Your new password must be different to previously used passwords.
        </p>
      </div>
      <div className="grid gap-6">
        <div className="grid gap-3">
          <Label htmlFor="password">New Password</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter new password"
              value={password}
              onChange={handlePasswordChange}
              onBlur={handlePasswordBlur}
              className={`h-11 pr-10 transition-all duration-200 focus:ring-2 ${
                touched.password && passwordError
                  ? 'border-destructive focus:ring-destructive/20'
                  : 'focus:ring-primary/20'
              }`}
              disabled={isLoading}
              required
              aria-invalid={touched.password && !!passwordError}
              aria-describedby={touched.password && passwordError ? 'password-error' : undefined}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1 rounded hover:bg-muted/50"
              tabIndex={-1}
              disabled={isLoading}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          {touched.password && passwordError && (
            <div
              id="password-error"
              className="flex items-center gap-1.5 text-sm text-destructive animate-in fade-in-0 slide-in-from-top-1"
            >
              <AlertCircle className="h-3.5 w-3.5" />
              <span>{passwordError}</span>
            </div>
          )}
        </div>
        <div className="grid gap-3">
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <div className="relative">
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              onBlur={handleConfirmPasswordBlur}
              className={`h-11 pr-10 transition-all duration-200 focus:ring-2 ${
                touched.confirmPassword && confirmPasswordError
                  ? 'border-destructive focus:ring-destructive/20'
                  : 'focus:ring-primary/20'
              }`}
              disabled={isLoading}
              required
              aria-invalid={touched.confirmPassword && !!confirmPasswordError}
              aria-describedby={touched.confirmPassword && confirmPasswordError ? 'confirm-password-error' : undefined}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1 rounded hover:bg-muted/50"
              tabIndex={-1}
              disabled={isLoading}
            >
              {showConfirmPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          {touched.confirmPassword && confirmPasswordError && (
            <div
              id="confirm-password-error"
              className="flex items-center gap-1.5 text-sm text-destructive animate-in fade-in-0 slide-in-from-top-1"
            >
              <AlertCircle className="h-3.5 w-3.5" />
              <span>{confirmPasswordError}</span>
            </div>
          )}
        </div>
        {error && (
          <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 animate-in fade-in-0 slide-in-from-top-2">
            <p className="text-sm text-destructive font-medium">{error}</p>
          </div>
        )}
        <Button
          type="submit"
          className="h-12"
          variant="primary"
          disabled={isLoading || (touched.password && !!passwordError) || (touched.confirmPassword && !!confirmPasswordError)}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <span className="h-4 w-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              Resetting...
            </span>
          ) : (
            "Set Password"
          )}
        </Button>
      </div>
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center justify-center gap-2 text-primary hover:underline transition-colors"
          disabled={isLoading}
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Login Page</span>
        </button>
        <div>
          <span className="text-sm font-normal">Didn&apos;t receive the email?</span>
          <button
            type="button"
            onClick={onBack}
            className="text-primary font-medium hover:underline transition-colors"
            disabled={isLoading}
          >
            {" "}Click to resend
          </button>
        </div>
      </div>
    </form>
  );
};

export default SetNewPasswordForm;
